// Detecção de bundle stale pós-deploy: um import dinâmico falha porque a aba
// roda o shell de uma versão anterior e o chunk pedido não existe mais no edge.
//
// A recuperação é automática — `recoverFromStaleBundle` recarrega a aba já na
// versão publicada (ver staleBundleRecovery.ts). O evento `alamo:chunk-stale`
// só é emitido quando as tentativas automáticas se esgotaram: aí o
// PwaUpdatePrompt mostra o toast e a decisão volta para o usuário.
//
// A falha pode ocorrer durante o BOOT, antes de o PwaUpdatePrompt montar e
// registrar o listener. Para não perder esse prompt, o último evento fica
// pendente em memória (consumePendingChunkStale) e o componente drena no mount.

import { isStaleBundleError, recoverFromStaleBundle } from './staleBundleRecovery';

export const CHUNK_STALE_EVENT = 'alamo:chunk-stale';

declare global {
  interface WindowEventMap {
    // Emitido quando o bundle stale não pôde ser resolvido sozinho. Não carrega
    // payload — o PwaUpdatePrompt só precisa saber que ocorreu.
    'alamo:chunk-stale': CustomEvent<undefined>;
  }
}

// Colapsa uma rajada de erros (vários lazy imports falhando em sequência) num
// único tratamento. Rearmado sempre que o prompt é consumido, para não engolir
// um 2º deploy legítimo mais tarde na mesma sessão.
let armed = true;

// Guarda o evento até que haja um listener (cobre falha no boot).
let pending = false;

/**
 * Ponto único da detecção: recarrega sozinho na versão publicada e, só quando
 * as tentativas automáticas acabaram, emite o evento para a UI pedir o clique.
 */
export function handleStaleBundle(): void {
  if (!armed) return;
  armed = false;

  if (recoverFromStaleBundle()) {
    if (import.meta.env.DEV) {
      console.warn('[chunkReload] bundle stale — recarregando na versão publicada');
    }
    return;
  }

  pending = true;
  window.dispatchEvent(new CustomEvent(CHUNK_STALE_EVENT));
}

// Consumido pelo PwaUpdatePrompt no mount: se um chunk falhou antes do listener
// existir, drena o evento perdido. Rearma o detector para permitir reemissão
// (2º deploy) — o toast em si é deduplicado por id no componente.
export function consumePendingChunkStale(): boolean {
  const wasPending = pending;
  pending = false;
  armed = true;
  return wasPending;
}

// Reseta o estado interno (arm/pending). Uso exclusivo de teste — o app importa
// este módulo uma única vez e nunca precisa reiniciá-lo.
export function __resetChunkReloadStateForTest(): void {
  armed = true;
  pending = false;
}

// Registra os listeners uma única vez por `window` (idempotente): o módulo é
// side-effect e não deve empilhar handlers se for reimportado.
const REGISTERED_FLAG = '__alamoChunkReloadRegistered__';
const win = window as typeof window & { [REGISTERED_FLAG]?: boolean };

if (!win[REGISTERED_FLAG]) {
  win[REGISTERED_FLAG] = true;

  // Vite emite `vite:preloadError` quando o preload de um chunk lazy falha. O
  // evento NÃO é cancelado: com `preventDefault()` o helper de preload passa a
  // resolver o `import()` com `undefined` em vez de rejeitar, e o React.lazy
  // quebra o render em "Cannot read properties of undefined (reading
  // 'default')" — um TypeError opaco, sem menção a chunk, antes de a recarga
  // acontecer. Deixando o Vite relançar, a falha chega ao error boundary como
  // erro de chunk legível.
  window.addEventListener('vite:preloadError', (() => {
    handleStaleBundle();
  }) as EventListener);

  // Falhas de import() fora do preload do Vite chegam como promise rejeitada.
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (!isStaleBundleError(event.reason)) return;
    event.preventDefault();
    handleStaleBundle();
  });
}
